/**
 * @file The Home page's year-to-date tiles, one per scope level the user may
 * see: "<label> — YTD" (My sales reads `selfHome`), booked with "booked", the
 * booked plus quoted bar against the goal, "+ $x quoted" beside "x% of $goal",
 * then the sub-label (the caller's name, team names joined, or Org sub-label) beside
 * the plan gap, compact and unsigned, "ahead of plan" in the accent or "behind
 * plan" in red. "Analytics →" opens the Overview at that level and YTD.
 * Consumer: `HomePageContent`, which places the tiles as cells of its row
 * beside Needs me, so the region lays out no box of its own. An unanswered
 * figure renders through `AnalyticsUnansweredFigure`, never as zero.
 */
import { FC, ReactNode, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Typography, theme } from 'antd';

import {
  ANALYTICS_SCOPE_LEVELS,
  analyticsCurrencyText,
  type AnalyticsScopeLevel,
} from '#types';
import {
  analyticsCompactCurrencyText,
  analyticsPercentText,
  analyticsViewSearch,
  useAnalyticsScopeLabels,
  useAnalyticsTeamNames,
  useAnalyticsUsers,
  type AnalyticsSummaryProgress,
  type GoalProgress,
} from '@/analytics';
import { AnalyticsAttainmentBar } from '@/components/analytics/AnalyticsAttainmentBar';
import { AnalyticsCardTitle } from '@/components/analytics/AnalyticsCardTitle';
import { AnalyticsCurrencyFigure } from '@/components/analytics/AnalyticsCurrencyFigure';
import { AnalyticsGoalState } from '@/components/analytics/AnalyticsGoalState';
import { AnalyticsUnansweredFigure } from '@/components/analytics/AnalyticsUnansweredFigure';

import FlexBox from '../../../atoms/FlexBox';
import FlexCard from '../../../atoms/FlexCard';
import { UserContext } from '../../../../providers/UserContext';
import { HomeSummaryProgress } from '../HomeSummaryProgress';

const { Text } = Typography;

type FoundProgress = Extract<AnalyticsSummaryProgress, { status: 'found' }>;

const GoalState: FC<{
  label: string;
  progress: FoundProgress;
  children: (progress: GoalProgress) => ReactNode;
}> = ({ label, progress: { booked, progress }, children }) =>
  booked.status === 'answered' ? (
    <AnalyticsGoalState
      progress={progress}
      loadingLabel={`Loading ${label} goal`}
    >
      {children}
    </AnalyticsGoalState>
  ) : (
    <AnalyticsUnansweredFigure figure={booked} />
  );

const PlanGap: FC<{ progress: GoalProgress }> = ({ progress }) => {
  const { token } = theme.useToken();
  const text = analyticsCompactCurrencyText(progress.aheadOfPlan);
  const isBehind = text.startsWith('-');
  return (
    <Text
      strong
      style={{ color: isBehind ? token.colorError : token.colorPrimary }}
    >
      {isBehind ? `${text.slice(1)} behind plan` : `${text} ahead of plan`}
    </Text>
  );
};

const FoundBody: FC<{
  label: string;
  subLabel: string;
  progress: FoundProgress;
}> = ({ label, subLabel, progress }) => {
  const { token } = theme.useToken();
  const set =
    progress.booked.status === 'answered' && progress.progress.status === 'set'
      ? progress.progress.progress
      : null;
  return (
    <>
      <FlexBox noGrow wrap justifyStart style={{ alignItems: 'baseline' }}>
        <Text strong style={{ fontSize: token.fontSizeHeading2 }}>
          <AnalyticsCurrencyFigure figure={progress.booked} />
        </Text>
        <Text type="secondary">booked</Text>
      </FlexBox>
      {set && (
        <FlexBox noGrow stretch>
          <AnalyticsAttainmentBar progress={set} />
        </FlexBox>
      )}
      <FlexBox noGrow stretch>
        <Text type="secondary">
          + <AnalyticsCurrencyFigure figure={progress.quoted} /> quoted
        </Text>
        <GoalState label={label} progress={progress}>
          {(goal) => (
            <Text type="secondary">
              {`${analyticsPercentText(goal.attainment)} of ${analyticsCurrencyText(goal.periodGoal)}`}
            </Text>
          )}
        </GoalState>
      </FlexBox>
      <FlexBox noGrow stretch>
        <Text type="secondary">{subLabel}</Text>
        {set && <PlanGap progress={set} />}
      </FlexBox>
    </>
  );
};

export const HomeAnalyticsTiles: FC<{
  progress: Readonly<Record<AnalyticsScopeLevel, AnalyticsSummaryProgress>>;
}> = ({ progress }) => {
  const { user } = useContext(UserContext);
  const uid = user?.uid ?? '';
  const labels = useAnalyticsScopeLabels();
  const users = useAnalyticsUsers(
    progress.self.status === 'notVisible' ? [] : [uid],
  );
  const ownTeams = users.teamsOf(uid);
  const teamName = useAnalyticsTeamNames(ownTeams);
  const subLabels: Record<AnalyticsScopeLevel, string> = {
    self: users.resolved ? users.nameOf(uid) : '',
    team: ownTeams.map(teamName).join(', '),
    org: labels.orgSubLabel,
  };
  const levels = ANALYTICS_SCOPE_LEVELS.filter(
    (level) => progress[level].status !== 'notVisible',
  );
  if (levels.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Year to date analytics"
      style={{ display: 'contents' }}
    >
      {levels.map((level) => {
        const title = `${level === 'self' ? labels.selfHome : labels[level]} — YTD`;
        return (
          <FlexCard key={level} role="group" aria-label={title} gap={15}>
            <FlexBox noGrow stretch style={{ alignItems: 'baseline' }}>
              <AnalyticsCardTitle title={title} />
              <Link
                to={{
                  pathname: '/analytics',
                  search: analyticsViewSearch({
                    tab: 'overview',
                    scope: level,
                    period: 'ytd',
                    status: null,
                    pastPromised: false,
                    project: null,
                  }),
                }}
                aria-label={`Open ${labels[level]} in Analytics`}
              >
                Analytics →
              </Link>
            </FlexBox>
            <HomeSummaryProgress
              progress={progress[level]}
              label={labels[level]}
              notVisibleTooltip="You cannot read this level"
            >
              {(found) => (
                <FoundBody
                  label={labels[level]}
                  subLabel={subLabels[level]}
                  progress={found}
                />
              )}
            </HomeSummaryProgress>
          </FlexCard>
        );
      })}
    </div>
  );
};
